/**
 * Motion tokens.
 *
 * Every animation primitive imports from here, so the whole site feels like one
 * system and there are no inline magic numbers to drift apart.
 */

/**
 * EASE.out is the signature: it decelerates for most of its duration, and that
 * long settling tail is what reads as "expensive" rather than "bouncy".
 */
export const EASE = {
  out: 'power3.out',
  in: 'power3.in',
  inOut: 'power2.inOut',
  /** For scrubbed timelines, where linear tracks the scrollbar honestly. */
  scrub: 'none',
} as const;

/** CSS equivalent of EASE.out, for transitions GSAP does not own. */
export const CSS_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

export const DUR = {
  fast: 0.4,
  base: 0.5,
  slow: 1.2,
  xslow: 1.8,
} as const;

export const STAGGER = {
  tight: 0.04, // characters
  word: 0.06, // words in a headline
  item: 0.09, // cards, list rows
  block: 0.16, // major blocks
} as const;

export const DISTANCE = {
  sm: 16,
  md: 18,
  lg: 64,
} as const;

/** Shared ScrollTrigger start: fire once the element is comfortably in view. */
export const START = 'top 94%';

/**
 * Breakpoints, mirroring the Tailwind theme so JS and CSS agree.
 * Used to build gsap.matchMedia() queries.
 */
export const BP = {
  md: 768,
  lg: 1024,
} as const;

/**
 * matchMedia query strings.
 *
 * Width and reduced-motion are combined here so both axes resolve in one place
 * instead of fighting each other in individual components.
 */
export const MQ = {
  /** Full choreography: desktop, motion allowed. */
  desktop: `(min-width: ${BP.lg}px) and (prefers-reduced-motion: no-preference)`,
  /** Tablet: reduced travel, shorter pins. */
  tablet: `(min-width: ${BP.md}px) and (max-width: ${BP.lg - 1}px) and (prefers-reduced-motion: no-preference)`,
  /** Mobile: no pinning at all — it fights native momentum scroll. */
  mobile: `(max-width: ${BP.md - 1}px) and (prefers-reduced-motion: no-preference)`,
  /** Anything at or above tablet, motion allowed. */
  mdUp: `(min-width: ${BP.md}px) and (prefers-reduced-motion: no-preference)`,
  /** Below desktop, motion allowed. The carousel translates its track here,
      where cards no longer all fit side by side. */
  belowLg: `(max-width: ${BP.lg - 1}px) and (prefers-reduced-motion: no-preference)`,
  /** Motion allowed at any width. */
  motion: '(prefers-reduced-motion: no-preference)',
  /** Motion suppressed. Content must still be fully visible. */
  reduced: '(prefers-reduced-motion: reduce)',
} as const;
